<script setup lang="ts">
import footerMenu from '@/mocks/footerMenu'
import { useRouteLink } from '@/composables/useRouteLink'

const { appURL } = useRuntimeConfig().public
const { locale } = useI18n()
const { footerData } = footerMenu()
const { routeLink } = useRouteLink()
</script>

<template>
  <footer class="footer">
    <div class="footer__wrapper">
      <div class="footer__contact">
        <div class="footer__contact_logos">
          <NuxtLink
            :to="`${appURL}${locale === 'en' ? '/en' : ''}`"
            title="BenefitSystems"
          >
            <img
              src="/logo.svg"
              class="footer__contact_logo"
            >
          </NuxtLink>

          <a
            href="https://bcorporation.net"
            title="B-Corporation"
            target="_blank"
          >
            <img
              src="/b-corp-logo.svg"
              class="footer__contact_logo-corp"
            >
          </a>
        </div>

        <div class="footer__contact_box">
          <div class="footer__contact_box-item">
            <p>Tel.</p><p>22 242 42 42</p>
          </div>

          <div class="footer__contact_box-item">
            <p>Email</p><p>infolinia@benefitsystems.pl</p>
          </div>
        </div>

        <div class="footer__iod">
          <p class="footer__contact_box-item">{{ $t('navigation.iod') }}</p>

          <p class="footer__contact_box-item">Katarzyna Zaklikocka</p>

          <p class="footer__contact_box-item">iod@benefitsystems.pl</p>
        </div>

        <div class="footer__contact_social">
          <p class="footer__contact_social-header">
            {{ $t('navigation.social') }}
          </p>

          <a
            href="https://pl.linkedin.com/company/benefit-systems-sa"
            target="_blank"
            title="BenefitSystems na Linkedin"
          >
            <img src="/icons/icon_linkedin.svg">
          </a>
        </div>

        <a
          v-if="locale === 'pl'"
          href="/newsletter"
          target="_blank"
          class="footer__newsletter"
        >
          Newsletter Wellbeing Lab
        </a>
      </div>

      <nav class="footer__navigation">
        <ul
          v-for="nav in footerData"
          :key="nav.label"
          class="footer__navigation_list"
        >
          <span class="footer__navigation_header">
            {{ $t(nav.label) }}
          </span>

          <li
            v-for="item in nav.children"
            :key="item.link"
            class="footer__navigation_item"
          >
            <NuxtLink
              v-if="item.link && !item.external"
              :to="routeLink($t(item.link))"
            >
              {{ $t(item.label) }}
            </NuxtLink>

            <a
              v-else-if="item.link"
              :href="$t(item.link)"
            >
              {{ $t(item.label) }}
            </a>
            
            <button
              v-if="item.fn"
              class="footer__navigation_item_button"
              @click="item.fn"
            >
              {{ $t(item.label) }}
            </button>
          </li>
        </ul>
      </nav>
    </div>
    
    <div class="footer__bottom">
      <p class="footer__bottom_rights">
        Benefit Systems S.A. Plac Europejski 2, 00-844 Warszawa, Sąd Rejonowy dla m.st. Warszawy w Warszawie, XIII Wydział Gospodarczy KRS,
        nr KRS 0000370919 NIP: 836-167-65-10, REGON: 750721670
      </p>
    </div>
  </footer>
</template>

<style lang="scss" scoped>
  .footer {
    padding: 4rem 2.4rem;
    font-weight: 500;
    background-color: $white;

    &__iod {
      margin-top: 3.2rem;
    }

    @include atM {
      padding: 7.2rem 4.8rem;
    }

    &__wrapper {
      @include atM {
        display: flex;
        gap: 7.8rem;
        max-width: 162.4rem;
        margin: 0 auto;
      }
    }

    &__navigation {
      display: flex;
      flex-direction: column;
      gap: 3rem;
      width: 100%;
      margin-top: 5rem;
      margin-bottom: 4rem;

      @include atXS {
        flex-direction: row;
        gap: 2.4rem;
        margin-bottom: 17.3rem;
      }

      &_list {
        @include atXS {
          flex: 1;
        }
      }

      &_header {
        display: block;
        margin-bottom: 2.4rem;
        padding-bottom: 1.6rem;
        color: $text-300;
        font-size: 1.8rem;
        line-height: 2.4rem;
        border-bottom: 1px solid $gray-400;

        @include atXS {
          margin-bottom: 4rem;
          padding-bottom: 1.6rem;
        }
      }

      &_item {
        margin-bottom: 1.6rem;
        color: $text-400;
        font-size: 2rem;
        line-height: 2.8rem;
        letter-spacing: -0.01em;
      }
    }

    &__contact {

      @include atM {
        min-width: 42.6rem;
      }

      &_logos {
        display: flex;
        align-items: center;
        gap: 5rem;
      }

      &_logo {
        width: 13rem;

        &-corp {
          width: 4rem;
        }
      }

      &_box {
        margin-top: 5rem;

        @include atM {
          margin-top: 7.2rem;
        }

        &-item {
          display: flex;
          align-items: center;
          margin-bottom: 0.8rem;
          font-weight: 500;
          font-size: 1.8rem;
          line-height: 2.4rem;

          p {
            width: 100%;
            height: 4.8rem;
            padding-top: 1.2rem;
            padding-bottom: 1.2rem;
            color: $text-300;
            border-bottom: 1px solid $gray-400;
          }

          p:first-of-type {
            width: 8rem;
            color: $text-400;
            border-right: 1px solid $gray-400;
          }
          p:nth-of-type(2) {
            padding-left: 1.6rem;
          }
        }
      }

      &_social {
        margin-top: 4rem;
        &-header {
          margin-bottom: 1.6rem;
          font-weight: 500;
          font-size: 1.8rem;
          line-height: 2.4rem;
        }
      }
    }

    &__newsletter {
      @include body-2-medium;
      display: block;
      margin-top: 2.4rem;
      color: $text-300;
      text-decoration: underline;
    }

    &__bottom {

      @include atM {
        display: flex;
        justify-content: space-between;
        align-items: center;
        max-width: 162.4rem;
        margin: 0 auto;
      }

      &_rights {
        max-width: 113.9rem;
        color: $text-300;
        font-weight: 500;
        font-size: 1.4rem;
        line-height: 2rem;
      }
    }

    &__buttons {
      display: flex;
      gap: 0.8rem;
      margin-top: 4rem;

      @include atM {
        margin-top: 0;
      }

      .button,
      .languages {
        width: 50%;
      }
    }

    &__navigation_item_button {
      cursor: pointer;
    }
  }
</style>
